import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { IWebInteractiveComponentProps } from "@faire/slate/private/shared/types";
import { TagBase } from "@faire/slate/private/tag/TagBase";
import { TagContent } from "@faire/slate/private/tag/TagContent";
import { TagDismissButton } from "@faire/slate/private/tag/TagDismissButton";
import {
  ISlateTagPropConstraints,
  TagVariant,
} from "@faire/slate/private/tag/types";
import React from "react";

type IProps = {
  /**
   * The tag variant.
   *
   * @default "neutral"
   */
  variant?: TagVariant;

  /**
   * The tag label.
   */
  label: string;

  /**
   * The dismiss handler, dismiss button is rendered if provided.
   *
   * @note not allowed if variant="success"|"warning"|"critical"
   */
  onDismiss?: () => void;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IWebInteractiveComponentProps;

/**
 * A tag contains a descriptive word or phrase associated with another object.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Tag-2242efb5c25a80749f87e230fa5a0f89 | Slate/Tag}
 */
export const Tag: React.FC<ISlateTagPropConstraints<IProps>> = ({
  label,
  onDismiss,
  variant = "neutral",
  ...props
}) => (
  <TagBase
    {...props}
    className={tagStyle.cls({ variant, className: componentCls() })}
    data-component-name="Slate/Tag"
  >
    <TagContent>{label}</TagContent>
    {onDismiss ? (
      <TagDismissButton tagLabel={label} onClick={onDismiss} />
    ) : null}
  </TagBase>
);

const tagStyle = tsv({
  variants: {
    variant: {
      neutral: "",
      success: "fs-tag--success",
      warning: "fs-tag--warning",
      critical: "fs-tag--critical",
      info: "fs-tag--info",
      strong: "fs-tag--strong",
    },
  },
});
